import type { CertGenParams } from '@yandex-int/k-common';

import { formatCityDate } from '../../utils/date/format-city-date';

interface Props extends CertGenParams {
    issue_date?: string;
}

export const TeacherReserveFinalist = ({
    school_name: schoolName,
    first_name: firstName,
    middle_name: middleName,
    last_name: lastName,
    issue_date: issueDate,
}: Props) => {
    const formattedDate = formatCityDate('Москва', issueDate);

    return `<!DOCTYPE html>
    <html lang="ru">
        <head>
            <title>Сертификат участника кадрового резерва</title>
            <meta http-equiv="Content-Type" content="text/html; charset=utf-8" />
            <meta name="viewport" content="width=device-width, initial-scale=1.0" />
            <style>
                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-light.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-light.woff') format('woff');
                    font-weight: 300;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-regular.woff') format('woff');
                    font-weight: 400;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-medium.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-medium.woff') format('woff');
                    font-weight: 500;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Text';
                    src: url('https://yastatic.net/s3/home/fonts/ys/1/text-bold.woff2') format('woff2'),
                        url('https://yastatic.net/s3/home/fonts/ys/1/text-bold.woff') format('woff');
                    font-weight: bold;
                    font-style: normal;
                }

                @font-face {
                    font-family: 'YS Display';
                    font-weight: bold;
                    font-style: normal;
                    font-stretch: normal;

                    src: url('//yastatic.net/s3/home/fonts/ys/1/display-bold.woff2') format('woff2'),
                        url('//yastatic.net/s3/home/fonts/ys/1/display-bold.woff') format('woff');
                }

                @font-face {
                    font-family: 'YS Display';
                    font-weight: 800;
                    font-style: normal;
                    font-stretch: normal;

                    src: url('//yastatic.net/s3/home/fonts/ys/1/display-heavy.woff2') format('woff2'),
                        url('//yastatic.net/s3/home/fonts/ys/1/display-heavy.woff') format('woff');
                }

                @page {
                    size: 297mm 210mm;
                    margin: 0;
                    padding: 0;
                }

                @media print {
                    html,
                    body {
                        width: 297mm;
                        height: 209.8mm;

                        box-sizing: border-box;
                    }
                }

                html {
                    position: relative;

                    margin: 0;
                    padding: 0;

                    --background-color: #ffffff;
                    --text-color: #000000;
                    --second-text-color: #363e45;
                }

                body {
                    margin: 0;

                    color: var(--text-color);

                    font-family: 'YS Text', sans-serif;
                }

                .page {
                    position: relative;

                    overflow: hidden;

                    box-sizing: border-box;

                    width: 297mm;
                    height: 209.8mm;

                    background: var(--background-color);
                }

                .page__background {
                    position: absolute;

                    width: 100%;
                    height: 100%;

                    overflow: hidden;

                    display: flex;

                    z-index: 1;
                }

                .page__background img {
                    display: block;
                }

                .page__sideblock {
                    position: absolute;

                    top: calc(297 / 842 * 42mm);
                    right: calc(210 / 595 * 64mm);

                    text-align: right;
                }

                .logo {
                    width: calc(210 / 595 * 159mm);
                    height: calc(297 / 842 * 20mm);
                }

                .upper-title {
                    font-size: 16pt;
                    font-weight: 700;
                    line-height: 20.8pt;
                    color: var(--second-text-color);
                    text-transform: uppercase;
                    margin-top: calc(297 / 842 * 14mm);
                }

                .page__content {
                    margin-left: calc(210 / 595 * 64mm);
                    margin-top: calc(297 / 842 * 40mm);
                }

                .content {
                    position: relative;

                    display: flex;
                    flex-direction: column;
                    box-sizing: border-box;

                    width: calc(210 / 595 * 304mm);
                    height: 100%;

                    color: var(--text-color);

                    z-index: 5;
                }

                .content__title {
                    font-weight: 500;
                    font-family: 'YS Display', 'Arial', sans-serif;
                    letter-spacing: 1%;
                    font-size: 54pt;
                    color: var(--second-text-color);
                }

                .content__subtitle {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14pt;
                    line-height: 14pt;
                    margin-top: calc(297 / 842 * 6mm);
                    color: var(--second-text-color);
                }

                .content__personal-info {
                    margin-top: calc(297 / 842 * 21mm);
                }

                .personal-info {
                    display: flex;
                    flex-direction: column;

                    height: calc(297 / 842 * 173mm);
                }

                .personal-info__name {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 500;
                    font-size: 28pt;
                    line-height: 30.75pt;
                }

                .personal-info__school {
                    margin-top: calc(297 / 842 * 12mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-style: normal;
                    font-weight: 400;
                    font-size: 14pt;
                    line-height: 16.8pt;
                }

                .content__description {
                    margin-top: calc(297 / 842 * 12mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 500;
                    font-size: 14pt;

                    height: calc(297 / 842 * 68mm);
                }

                .footer {
                    position: absolute;

                    top: calc(297 / 842 * 421mm);
                    left: 0;
                }

                .footer__date {
                    margin-top: calc(297 / 842 * 22mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 400;
                    font-size: 10pt;
                    line-height: 13pt;
                }

                .person {
                    display: flex;
                    flex-direction: column;

                    margin-top: calc(297 / 842 * 6mm);
                }

                .person__name {
                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 700;
                    font-size: 12pt;
                    line-height: 15pt;
                }

                .person__position {
                    margin-top: calc(297 / 842 * 8mm);

                    font-family: 'YS Text', 'Arial', sans-serif;
                    font-weight: 400;
                    font-size: 12pt;
                    line-height: 14pt;
                }

                .content__seal {
                    position: absolute;
                    width: calc(210 / 595 * 181mm);
                    height: calc(297 / 842 * 154mm);

                    top: calc(297 / 842 * 390mm);
                    left: calc(210 / 595 * 127mm);
                }

                .seal {
                    height: 100%;
                    width: 100%;
                }

                .signature {
                    position: absolute;

                    height: calc(297 / 842 * 75mm);
                    width: calc(210 / 595 * 120mm);

                    top: calc(297 / 842 * 37mm);
                    left: calc(210 / 595 * 12mm);
                }
            </style>
        </head>
        <body>
            <div class="page">
                <div class="page__background">
                    <img src="https://e7n.s3.yandex.net/static/certificates/teacher-reserve/back_final.png" />
                </div>

                <div class="page__sideblock">
                    <img
                        class="logo"
                        src="https://e7n.s3.yandex.net/static/certificates/2022-autumn-diag-inf/logo.svg"
                        alt="Яндекс Учебник"
                    />

                    <div class="upper-title">Кадровый резерв<br />учителей информатики</div>
                </div>

                <div class="page__content content content__main">
                    <div class="content__title">Сертификат</div>
                    <div class="content__subtitle">подтверждает, что</div>

                    <div class="content__personal-info personal-info">
                        <div class="personal-info__name name">
                            ${lastName ? `${lastName}` : ''}<br />
                            ${firstName ? `${firstName}` : ''}<br />
                            ${middleName ? `${middleName}` : ''}
                        </div>
                        <div class="personal-info__school school">${schoolName ? `${schoolName}` : ''}</div>
                    </div> 
                    <div class="content__description">прошёл программу профессионального развития от компании Яндекс и по итогам конкурсного отбора был включен в Кадровый резерв учителей информатики</div>

                    <div class="content__footer footer">
                        <div class="footer__person person">
                            <div class="person__name">Елена Краевская</div>
                            <div class="person__position">
                                Руководитель<br />Яндекс Учебника
                            </div>
                        </div>
                        <div class="footer__date">${formattedDate}</div>
                    </div>

                    <div class="content__seal">
                        <img
                            class="seal"
                            src="https://avatars.mds.yandex.net/get-schoolbook-images/5623482/c70lYm0daoqvl4gRjWXP1/orig"
                            alt="подпись"
                        />
                    </div>
                </div>
            </div>
        </body>
    </html>
    `;
};
